import { formatInTimeZone } from 'date-fns-tz';
import { ru } from 'date-fns/locale';

/** All dates are shown in the studio's time zone, never the device's. */
export function formatInStudio(value: string | Date, timezone: string, pattern: string) {
  return formatInTimeZone(typeof value === 'string' ? new Date(value) : value, timezone, pattern, { locale: ru });
}

export const formatDateLong = (value: string | Date, tz: string) => formatInStudio(value, tz, 'd MMMM, EEEE');
export const formatDateShort = (value: string | Date, tz: string) => formatInStudio(value, tz, 'd MMM, EEEEEE');
export const formatTime = (value: string | Date, tz: string) => formatInStudio(value, tz, 'HH:mm');
export const formatDateTime = (value: string | Date, tz: string) => formatInStudio(value, tz, 'd MMMM, EEEEEE · HH:mm');

/** Local calendar date YYYY-MM-DD → "12 октября, пн" without time-zone shifts. */
export function formatLocalDay(isoDate: string, pattern = 'd MMMM, EEEEEE') {
  return formatInTimeZone(new Date(`${isoDate}T12:00:00Z`), 'UTC', pattern, { locale: ru });
}

export function formatPrice(minor: number, currency = 'RUB', kind: 'fixed' | 'from' = 'fixed') {
  const value = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    maximumFractionDigits: minor % 100 === 0 ? 0 : 2,
  }).format(minor / 100);
  return kind === 'from' ? `от ${value}` : value;
}

export function formatDuration(minutes: number) {
  if (minutes >= 1440 && minutes % 1440 === 0) {
    const days = minutes / 1440;
    return `${days} ${plural(days, 'день', 'дня', 'дней')}`;
  }
  if (minutes >= 1440) {
    const hours = Math.round(minutes / 60);
    return `${hours} ч`;
  }
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h} ч ${m} мин`;
  if (h) return `${h} ч`;
  return `${m} мин`;
}

export function plural(n: number, one: string, few: string, many: string) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/** "+79001234567" → "+7 900 123-45-67" (Russian numbers); other numbers stay as they are. */
export function formatPhone(phone: string) {
  const d = phone.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('7')) return `+7 ${d.slice(1, 4)} ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9)}`;
  return phone;
}

export function formatPhoneHref(phone: string) {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}

const WEEKDAYS = ['', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
export const weekdayShort = (isoWeekday: number) => WEEKDAYS[isoWeekday] ?? '';

/** "Пн–Пт 09:00–21:00 · Сб 10:00–20:00 · Вс выходной" from weekly intervals. */
export function summarizeHours(hours: Array<{ weekday: number; opensAt: string; closesAt: string }>) {
  return hoursRows(hours).map((row) => `${row.days} ${row.text}`);
}

/** Opening hours grouped into runs of days with the same hours: «Пн–Пт» / «09:00–21:00». */
export function hoursRows(hours: Array<{ weekday: number; opensAt: string; closesAt: string }>) {
  const byDay = new Map<number, string>();
  for (let d = 1; d <= 7; d += 1) {
    const intervals = hours.filter((h) => h.weekday === d).map((h) => `${h.opensAt}–${h.closesAt}`);
    byDay.set(d, intervals.length ? intervals.join(', ') : 'выходной');
  }
  const groups: Array<{ from: number; to: number; text: string }> = [];
  for (let d = 1; d <= 7; d += 1) {
    const text = byDay.get(d)!;
    const last = groups.at(-1);
    if (last && last.text === text && last.to === d - 1) last.to = d;
    else groups.push({ from: d, to: d, text });
  }
  return groups.map((g) => ({
    days: g.from === g.to ? weekdayShort(g.from) : `${weekdayShort(g.from)}–${weekdayShort(g.to)}`,
    text: g.text,
  }));
}
